import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Linkedin, Twitter, Mail, MessageCircle, Copy, Share2, Check } from "lucide-react";
import { copyToClipboard } from "@/lib/clipboard";
import { toast } from "sonner";

/**
 * Re-usable "Refer a partner" share modal for affiliates.
 *
 * Pre-bakes platform-specific copy and opens the native share URL
 * (LinkedIn / X / WhatsApp / Email) with the affiliate's tracking link
 * encoded so every share is attributable.
 */

const TEMPLATES = (link) => ({
  linkedin: `I've been earning passive recurring commission with the @Connexis Partner Programme — they're the operational backbone behind financial customer acquisition (£15bn routed monthly).

Up to 40% recurring commission for the lifetime of every account you refer. Paid weekly via Stripe.

If you sell into fintech, lending, or insurance, this is the cleanest infrastructure partner I've come across.

${link}`,

  twitter: `Found the cleanest fintech infra programme I've seen — up to 40% recurring commission for the lifetime of every referral, paid weekly via Stripe.

If you work with lending/insurance/fintech brands, take a look 👇

${link}`,

  whatsapp: `Hey — thought you'd find this interesting. Connexis runs the routing infrastructure behind most UK financial advertisers (£15bn/month) and they have a partner programme paying up to 40% recurring commission. Worth a look 👉 ${link}`,

  email: `Subject: A partner programme worth your time\n\nHi,\n\nI've been working with Connexis — they're the operational backbone behind financial customer acquisition (lead routing, AI qualification, compliance, billing — all one platform).\n\nTheir partner programme pays up to 40% recurring commission for the lifetime of every referred account, paid weekly via Stripe Connect Express. It's by far the strongest in the fintech infrastructure space.\n\nIf you work with financial brands, you should take a look:\n${link}\n\nBest,`,
});

const SHARE_URLS = (text, link) => ({
  linkedin: `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}`,
  twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
  whatsapp: `https://wa.me/?text=${encodeURIComponent(text)}`,
  email: `mailto:?subject=${encodeURIComponent("A partner programme worth your time")}&body=${encodeURIComponent(text.replace(/^Subject:.*\n\n/, ""))}`,
});

export default function ReferPartnerModal({ open, onOpenChange, trackingLink }) {
  const [channel, setChannel] = useState("linkedin");
  const [text, setText] = useState(TEMPLATES(trackingLink).linkedin);
  const [copied, setCopied] = useState(false);

  // Refresh template when link changes
  React.useEffect(() => {
    setText(TEMPLATES(trackingLink)[channel]);
  }, [trackingLink, channel]);

  const channels = [
    { id: "linkedin", label: "LinkedIn", Icon: Linkedin, accent: "bg-[#0A66C2] hover:bg-[#084d96] text-white" },
    { id: "twitter",  label: "X",        Icon: Twitter,  accent: "bg-slate-900 hover:bg-slate-800 text-white" },
    { id: "whatsapp", label: "WhatsApp", Icon: MessageCircle, accent: "bg-[#25D366] hover:bg-[#1FAE52] text-white" },
    { id: "email",    label: "Email",    Icon: Mail,     accent: "bg-slate-200 hover:bg-slate-300 text-slate-900" },
  ];

  const doCopy = async () => {
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopied(true);
      toast.success("Copy ready — paste anywhere you like.");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Couldn't copy to clipboard");
    }
  };

  const doShare = () => {
    const url = SHARE_URLS(text, trackingLink)[channel];
    window.open(url, "_blank", "noopener,noreferrer,width=720,height=640");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="refer-partner-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Share2 className="size-5 text-[#0055FF]" /> Refer a partner
          </DialogTitle>
          <DialogDescription>
            Earn 5% override on every Connexis partner you bring into the programme — recurring, no cap.
          </DialogDescription>
        </DialogHeader>

        {/* Channel selector */}
        <div className="grid grid-cols-4 gap-2">
          {channels.map((c) => {
            const active = channel === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setChannel(c.id)}
                className={`flex flex-col items-center gap-1.5 py-3 rounded-lg border transition-all duration-150 text-sm
                  ${active
                    ? "border-[#0055FF] bg-[#0055FF]/5 text-[#0055FF] ring-1 ring-[#0055FF]/20"
                    : "border-slate-200 hover:border-slate-300 text-slate-700"}`}
                data-testid={`refer-channel-${c.id}`}
              >
                <c.Icon className="size-4" />
                <span>{c.label}</span>
              </button>
            );
          })}
        </div>

        {/* Editable share copy */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Share copy</label>
            <span className="text-[11px] text-slate-400 font-mono tabular-nums">{text.length} chars</span>
          </div>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="min-h-[160px] font-mono text-[12.5px] resize-none"
            data-testid="refer-share-text"
          />
        </div>

        {/* Tracking link readout */}
        <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2.5 flex items-center justify-between text-sm">
          <span className="font-mono text-[12px] text-slate-700 truncate flex-1">{trackingLink}</span>
          <button
            onClick={async () => {
              await copyToClipboard(trackingLink);
              toast.success("Tracking link copied");
            }}
            className="ml-3 inline-flex items-center gap-1.5 text-[#0055FF] hover:text-[#0044CC] text-[12px] font-medium shrink-0"
            data-testid="refer-copy-link"
          >
            <Copy className="size-3" /> link
          </button>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={doCopy} className="rounded-full" data-testid="refer-copy-copy">
            {copied ? <><Check className="size-4 text-emerald-600" /> Copied</> : <><Copy className="size-4" /> Copy text</>}
          </Button>
          <Button onClick={doShare} className={`rounded-full ${channels.find(c => c.id === channel)?.accent}`} data-testid="refer-share-go">
            <Share2 className="size-4" /> Share on {channels.find(c => c.id === channel)?.label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
